import {useMemo, useState} from "react";
import {App as AntdApp, Alert, Segmented, Table, Tabs, Modal, Input, Tag} from "antd";
import className from "classnames/bind";
import {Button, ExportButtons, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetGaOverviewQuery, useGetGaDimensionQuery, useDisconnectGaMutation} from "~/reduxs/api/gaApiSlice";
import {TrendBadge} from "~/features/GscInsight/components/GscTrend";
import GaTrendChart from "./GaTrendChart";
import GaPropertyPicker from "./GaPropertyPicker";
import style from "~/features/GscInsight/style/Gsc.module.scss";

const cn = className.bind(style);

/** Trừ (n-1) ngày từ chuỗi Y-m-d (mốc bắt đầu cho preset khoảng ngày). */
function minusDays(ymd, n) {
	const d = new Date(`${ymd}T00:00:00Z`);
	d.setUTCDate(d.getUTCDate() - (n - 1));
	return d.toISOString().slice(0, 10);
}

const RANGES = [
	{label: '7 ngày', value: 7},
	{label: '28 ngày', value: 28},
	{label: '3 tháng', value: 90},
	{label: '13 tháng', value: 395},
];

const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');
const pct = (v) => `${((v || 0) * 100).toFixed(1)}%`;

/** Dựng dữ liệu xuất Excel/PDF cho báo cáo GA4 (tổng quan + 5 chiều đã fetch sẵn). */
function buildGaExport({connection, from, to, overview, dims}) {
	const summary = overview?.summary || {};
	const delta = overview?.delta || {};

	const overviewRows = [
		['Phiên (Sessions)', Number(summary.sessions || 0), delta.sessions ?? ''],
		['Người dùng', Number(summary.total_users || 0), delta.total_users ?? ''],
		['Người dùng mới', Number(summary.new_users || 0), delta.new_users ?? ''],
		['Tỉ lệ tương tác', pct(summary.engagement_rate), delta.engagement_rate ?? ''],
		['Tỉ lệ thoát (bounce)', pct(summary.bounce_rate), delta.bounce_rate ?? ''],
		['Thời gian tương tác TB/phiên (giây)', Number(summary.avg_engagement_sec || 0), ''],
		['Sự kiện chính', Number(summary.key_events || 0), delta.key_events ?? ''],
		['Lượt xem trang', Number(summary.page_views || 0), delta.page_views ?? ''],
		['Doanh thu', Math.round(summary.revenue || 0), delta.revenue ?? ''],
	];
	const seriesRows = (overview?.series || []).map((d) => [
		d.date, d.sessions, d.total_users, d.key_events, Math.round(d.revenue || 0),
	]);

	// 1 sheet chiều: metric chính theo BE trả (page_views cho trang, sessions cho phần còn lại).
	const dimSheet = (label, data) => {
		const metric = data?.metric || 'sessions';
		const head = [label, metric === 'page_views' ? 'Lượt xem' : 'Phiên', 'Người dùng', 'Sự kiện chính', 'Doanh thu', 'Thay đổi (%)'];
		const rows = (data?.rows || []).map((r) => [
			DIM_VALUE_LABEL[r.value] || r.value, r[metric], r.total_users, r.key_events ?? '', Math.round(r.revenue || 0), r.delta_pct ?? '',
		]);
		return {head, rows};
	};

	const buildExcel = () => [
		{name: 'Tổng quan', aoa: [
			['Chỉ số', 'Giá trị', 'So kỳ trước (%)'], ...overviewRows,
			[], ['Ngày', 'Phiên', 'Người dùng', 'Sự kiện chính', 'Doanh thu'], ...seriesRows,
		]},
		...dims.map(({name, label, data}) => {
			const {head, rows} = dimSheet(label, data);
			return {name, aoa: [head, ...rows]};
		}),
	];

	const buildPdf = () => {
		const channels = dims.find((d) => d.name === 'Kênh');
		const pages = dims.find((d) => d.name === 'Trang');
		const channelSheet = dimSheet(channels?.label || 'Kênh', channels?.data);
		const pageSheet = dimSheet(pages?.label || 'Trang', pages?.data);

		return {
			title: 'Báo cáo Google Analytics 4',
			subtitle: `${connection.property_name || connection.property} · ${from} → ${to}`,
			sections: [
				{heading: 'Tổng quan', columns: ['Chỉ số', 'Giá trị', 'So kỳ trước (%)'], rows: overviewRows},
				{heading: 'Kênh', columns: channelSheet.head, rows: channelSheet.rows},
				{heading: `Top trang (${Math.min(pageSheet.rows.length, 25)}/${pageSheet.rows.length})`,
					columns: pageSheet.head, rows: pageSheet.rows.slice(0, 25)},
			],
		};
	};

	return {filename: `bao-cao-ga4-${connection.property_name || connection.property}-${from}-${to}`, buildExcel, buildPdf};
}

/** Nhãn tiếng Việt cho giá trị chiều quen thuộc (channel/device). */
const DIM_VALUE_LABEL = {
	'Organic Search': 'Tìm kiếm tự nhiên',
	'Direct': 'Truy cập trực tiếp',
	'Referral': 'Giới thiệu (referral)',
	'Organic Social': 'Mạng xã hội',
	'Paid Search': 'Tìm kiếm trả phí',
	'Paid Social': 'MXH trả phí',
	'Email': 'Email',
	'Display': 'Hiển thị (display)',
	'Unassigned': 'Chưa phân loại',
	'desktop': 'Máy tính',
	'mobile': 'Di động',
	'tablet': 'Máy tính bảng',
};

/**
 * Bảng top 1 chiều GA4 (kênh/nguồn/trang/quốc gia/thiết bị) — tự fetch theo dim.
 * Cột metric chính theo BE trả (`page_views` cho trang, `sessions` cho phần còn lại) + Thay đổi.
 */
function GaDimTable({projectId, dim, from, to, label}) {
	const {data, isFetching} = useGetGaDimensionQuery({projectId, dim, from, to, limit: 100});

	const rows = data?.rows || [];
	const metric = data?.metric || 'sessions';
	const isPage = metric === 'page_views';
	const hasRevenue = rows.some((r) => r.revenue > 0);

	const columns = [
		{
			title: label,
			dataIndex: 'value',
			key: 'value',
			ellipsis: true,
			render: (v) => DIM_VALUE_LABEL[v] || v,
		},
		{
			title: isPage ? 'Lượt xem' : 'Phiên',
			dataIndex: metric,
			key: metric,
			align: 'right',
			width: 110,
			sorter: (a, b) => a[metric] - b[metric],
			render: (v) => <strong>{fmt(v)}</strong>,
		},
		{title: 'Người dùng', dataIndex: 'total_users', key: 'total_users', align: 'right', width: 120, render: fmt},
		// Cột phụ — ẩn dưới 768px cho gọn màn nhỏ.
		...(!isPage ? [{title: 'Sự kiện chính', dataIndex: 'key_events', key: 'key_events', align: 'right', width: 120, responsive: ['md'], render: fmt}] : []),
		...(hasRevenue ? [{title: 'Doanh thu', dataIndex: 'revenue', key: 'revenue', align: 'right', width: 130, responsive: ['md'], render: (v) => fmt(Math.round(v))}] : []),
		{
			title: 'Thay đổi',
			dataIndex: 'delta_pct',
			key: 'delta_pct',
			align: 'right',
			width: 110,
			render: (v) => <TrendBadge value={v} />,
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={rows}
			rowKey="value"
			loading={isFetching}
			size="small"
			pagination={{pageSize: 20, hideOnSinglePage: true}}
			locale={{emptyText: 'Chưa có dữ liệu trong khoảng thời gian này.'}}
			scroll={{x: 'max-content'}}
		/>
	);
}

/**
 * Báo cáo GA4 phục vụ từ DB (mirror GscReports): thẻ tổng quan (phiên/người dùng/
 * engagement/sự kiện chính/doanh thu) kèm % so kỳ trước + biểu đồ xu hướng + tab
 * Kênh/Nguồn/Trang/Quốc gia/Thiết bị. Chọn khoảng ngày, đổi property, ngắt kết nối.
 */
function GaReports({projectId, connection}) {

	const {notification} = AntdApp.useApp();
	const [days, setDays] = useState(28);
	const [changing, setChanging] = useState(false);

	const [disconnectOpen, setDisconnectOpen] = useState(false);
	const [confirmText, setConfirmText] = useState('');

	const freshest = connection?.freshest_date;

	const {from, to} = useMemo(() => {
		if (!freshest) return {from: undefined, to: undefined};
		return {from: minusDays(freshest, days), to: freshest};
	}, [freshest, days]);

	const {data: overview} = useGetGaOverviewQuery({projectId, from, to});
	const [disconnect, {isLoading: disconnecting}] = useDisconnectGaMutation();

	// Dữ liệu 5 chiều cho nút xuất báo cáo — cùng tham số với GaDimTable nên RTK Query
	// dùng chung cache (mở tab tương ứng không gọi API lần nữa).
	const dimArgs = (dim) => ({projectId, dim, from, to, limit: 100});
	const {data: dimChannels}  = useGetGaDimensionQuery(dimArgs('channels'), {skip: !from});
	const {data: dimSources}   = useGetGaDimensionQuery(dimArgs('sources'), {skip: !from});
	const {data: dimPages}     = useGetGaDimensionQuery(dimArgs('pages'), {skip: !from});
	const {data: dimCountries} = useGetGaDimensionQuery(dimArgs('countries'), {skip: !from});
	const {data: dimDevices}   = useGetGaDimensionQuery(dimArgs('devices'), {skip: !from});

	// Xác nhận ngắt kết nối: gõ lại tên property (mirror UX của GSC — mất 13 tháng backfill).
	const confirmTarget = connection.property_name || connection.property;
	const confirmMatched = confirmText.trim() === confirmTarget;

	const handleDisconnect = async () => {
		if (!confirmMatched) return;
		try {
			await disconnect(projectId).unwrap();
			notification.success({message: 'Đã ngắt kết nối'});
			setDisconnectOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không ngắt được kết nối')});
		}
	};

	if (changing) {
		return <GaPropertyPicker projectId={projectId} email={connection?.email} onCancel={() => setChanging(false)} />;
	}

	const summary = overview?.summary || {};
	const delta   = overview?.delta || {};
	const hasRevenue = (summary.revenue || 0) > 0;

	const cards = [
		{label: 'Phiên (Sessions)', color: '#e97316', value: fmt(summary.sessions), delta: delta.sessions},
		{label: 'Người dùng', color: '#0ea5e9', value: fmt(summary.total_users), delta: delta.total_users},
		{label: 'Người dùng mới', color: '#16a34a', value: fmt(summary.new_users), delta: delta.new_users},
		{label: 'Tỉ lệ tương tác', color: '#7c3aed', value: `${((summary.engagement_rate || 0) * 100).toFixed(1)}%`, delta: delta.engagement_rate},
		{label: 'Sự kiện chính', color: '#d97706', value: fmt(summary.key_events), delta: delta.key_events},
		...(hasRevenue
			? [{label: 'Doanh thu', color: '#059669', value: fmt(Math.round(summary.revenue)), delta: delta.revenue}]
			: [{label: 'Lượt xem trang', color: '#059669', value: fmt(summary.page_views), delta: delta.page_views}]),
	];

	const overviewTab = (
		<>
			<div className={cn('cards')}>
				{cards.map((c) => (
					<div key={c.label} className={cn('card')}>
						<div className={cn('cardLabel')}><span className={cn('dot')} style={{background: c.color}} /> {c.label}</div>
						<div className={cn('cardValue')}>{c.value}</div>
						<div className={cn('cardDelta')}><TrendBadge value={c.delta} /> <span className={cn('cardDeltaHint')}>so kỳ trước</span></div>
					</div>
				))}
			</div>

			{/* Chỉ số phụ: bounce + thời gian tương tác TB (không đủ "thẻ" riêng). */}
			<div style={{display: 'flex', gap: 16, flexWrap: 'wrap', margin: '4px 0 8px', fontSize: 13, color: '#6b7280'}}>
				<span>Tỉ lệ thoát (bounce): <strong>{((summary.bounce_rate || 0) * 100).toFixed(1)}%</strong> <TrendBadge value={delta.bounce_rate} /></span>
				<span>Thời gian tương tác TB/phiên: <strong>{fmt(summary.avg_engagement_sec)}s</strong></span>
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Xu hướng theo ngày</h3>
				<GaTrendChart series={overview?.series || []} />
			</div>
		</>
	);

	const tabs = [
		{key: 'overview', label: 'Tổng quan', children: overviewTab},
		{key: 'channels', label: 'Kênh', children: <GaDimTable projectId={projectId} dim="channels" from={from} to={to} label="Kênh" />},
		{key: 'sources', label: 'Nguồn', children: <GaDimTable projectId={projectId} dim="sources" from={from} to={to} label="Nguồn / phương tiện" />},
		{key: 'pages', label: 'Trang', children: <GaDimTable projectId={projectId} dim="pages" from={from} to={to} label="Trang" />},
		{key: 'countries', label: 'Quốc gia', children: <GaDimTable projectId={projectId} dim="countries" from={from} to={to} label="Quốc gia" />},
		{key: 'devices', label: 'Thiết bị', children: <GaDimTable projectId={projectId} dim="devices" from={from} to={to} label="Thiết bị" />},
	];

	return (
		<div>
			{/* Thanh thông tin kết nối + hành động */}
			<div className={cn('section')} style={{paddingTop: 14, paddingBottom: 14}}>
				<div className={cn('rangeBar')} style={{marginBottom: 0}}>
					<div>
						<div style={{fontWeight: 600}}>
							{connection.property_name || connection.property}{' '}
							<Tag style={{marginLeft: 4}}>{connection.property}</Tag>
						</div>
						<div className={cn('metaRow')}>
							{connection.email && <span><FontAwesomeIcon icon="fa-brands fa-google" /> {connection.email}</span>}
							{connection.last_synced_date && <span>Dữ liệu tới ngày {connection.last_synced_date}</span>}
						</div>
					</div>
					<div style={{display: 'flex', gap: 8}}>
						<Button white small onClick={() => setChanging(true)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-arrows-rotate" />}>Đổi property</Button>
						<Button red small onClick={() => { setConfirmText(''); setDisconnectOpen(true); }}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-link-slash" />}>Ngắt kết nối</Button>
					</div>
				</div>
			</div>

			{connection.backfill_done === false && (
				<Alert type="info" showIcon style={{marginBottom: 16}}
					message="Đang đồng bộ dữ liệu lịch sử (tối đa 13 tháng)"
					description="Số liệu sẽ đầy dần trong nền. Bạn có thể xem trước phần dữ liệu đã có." />
			)}

			<Alert type="warning" showIcon banner style={{marginBottom: 16}}
				message={`Lưu ý: GA4 trễ 1–2 ngày. Dữ liệu mới nhất tới ${freshest || '—'}.`} />

			<div className={cn('rangeBar')}>
				<Segmented options={RANGES} value={days} onChange={setDays} />
				<div style={{display: 'flex', alignItems: 'center', gap: 12}}>
					<span style={{color: '#9ca3af', fontSize: 12}}>{from} → {to}</span>
					<ExportButtons {...buildGaExport({connection, from, to, overview, dims: [
						{name: 'Kênh', label: 'Kênh', data: dimChannels},
						{name: 'Nguồn', label: 'Nguồn / phương tiện', data: dimSources},
						{name: 'Trang', label: 'Trang', data: dimPages},
						{name: 'Quốc gia', label: 'Quốc gia', data: dimCountries},
						{name: 'Thiết bị', label: 'Thiết bị', data: dimDevices},
					]})} />
				</div>
			</div>

			<div className={cn('section')}>
				<Tabs items={tabs} defaultActiveKey="overview" />
			</div>

			{/* Xác nhận ngắt kết nối: mất 13 tháng backfill, không undo → bắt gõ lại property */}
			<Modal
				open={disconnectOpen}
				title="Ngắt kết nối Google Analytics 4"
				okText="Ngắt kết nối"
				okButtonProps={{danger: true, disabled: !confirmMatched}}
				confirmLoading={disconnecting}
				cancelText="Huỷ"
				onOk={handleDisconnect}
				onCancel={() => setDisconnectOpen(false)}
			>
				<Alert type="error" showIcon style={{marginBottom: 14}}
					message="Hành động không thể hoàn tác"
					description="Toàn bộ số liệu GA4 đã đồng bộ (backfill tối đa 13 tháng) của dự án này sẽ bị xoá. Kết nối lại sẽ phải đồng bộ từ đầu." />
				<p style={{marginBottom: 8}}>
					Gõ lại chính xác <code style={{background: '#f3f4f6', padding: '1px 6px', borderRadius: 4}}>{confirmTarget}</code> để xác nhận:
				</p>
				<Input
					value={confirmText}
					onChange={(e) => setConfirmText(e.target.value)}
					placeholder={confirmTarget}
					onPressEnter={handleDisconnect}
					status={confirmText && !confirmMatched ? 'error' : undefined}
				/>
			</Modal>
		</div>
	);
}

export default GaReports;
